<template>
  <layout class="trips-page-container">
    <div class="w-full flex adm-container sm:flex-col lg:flex-row">
      <div class="search-input sm:mb-4 sm:w-full lg:mb-0 lg:w-1/4 sm:mr-0 lg:mr-4">
        <div class="w-full h-full flex items-center justify-center">
          <div class="w-full">
            <suggestion-input
              :list="driverList"
              :lookup-fields="['name', 'number']"
              accessor="name"
              placeholder="Search drivers by name or mobile number"
              icon-class="fa-search"
              icon-position="left"
              @autocomplete-item-set="onAutoCompleteItemSelected"
              @autocomplete-cleared="onAutoCompleteItemCleared"
            />
          </div>
        </div>
      </div>
      <div class="filter-dropdown-container sm:mb-4 sm:w-full lg:mb-0 lg:w-1/4 sm:mr-0 lg:mr-4">
        <pt-dropdown
          :right="false"
          :text="statusDropDownItem[selectedStatus]"
          class="w-full"
        >
          <pt-dropdown-item
            :class="{'pt-dropdown-item-active': selectedStatus === 'All'}"
            @dd-item-clicked="filterByStatus('All')">{{ statusDropDownItem['All'] }}</pt-dropdown-item>
          <pt-dropdown-item
            :class="{'pt-dropdown-item-active': selectedStatus === 'COMPLETED'}"
            @dd-item-clicked="filterByStatus('COMPLETED')"
          >{{ statusDropDownItem['COMPLETED'] }}</pt-dropdown-item>
          <pt-dropdown-item
            :class="{'pt-dropdown-item-active': selectedStatus === 'CANCELED BY DRIVER'}"
            @dd-item-clicked="filterByStatus('CANCELED BY DRIVER')"
          >{{ statusDropDownItem['CANCELED BY DRIVER'] }}</pt-dropdown-item>
          <pt-dropdown-item
            :class="{'pt-dropdown-item-active': selectedStatus === 'CANCELED BY USER'}"
            @dd-item-clicked="filterByStatus('CANCELED BY USER')"
          >{{ statusDropDownItem['CANCELED BY USER'] }}</pt-dropdown-item>
        </pt-dropdown>
      </div>
      <div class="flex items-center justify-end text-primary font-bold sm:w-full lg:w-1/4 sm:my-4 lg:my-0">
        <pt-datepicker
          :value="date"
          :range="weekRange"
          :week-range="weekRange"
          :show-next-month-dates="weekRange"
          :show-prev-month-dates="weekRange"
          :close-on-range-select="true"
          class="flex flex-col flex-1 sm:pl-0 lg:pl-4"
          @selected="setDate"
        >
          <div
            slot="afterDayPicker"
            class="p-4"
          >
            <pt-radio-group
              v-model="selectedDateRangeType"
              :options="dateSelectionType"
              name="dateSelection"
            />
          </div>
        </pt-datepicker>
      </div>
      <div class="flex items-center justify-end text-primary font-bold sm:w-full lg:w-1/4">
        <download-csv
          :download-url="csvUrl"
          file-name="Trips List"
        />
      </div>
    </div>
    <div class="w-full flex flex-col adm-container text-grey-darker">
      <pt-list :is-collapsible="true">
        <div class="p-4 font-bold border-b flex-1 sm:hidden lg:flex">
          <i class="fa fa-chevron-right invisible" />
          <div class="w-1/6 ml-1">Requested On</div>
          <div class="w-1/6">Driver</div>
          <div class="w-1/6">Reg. Number</div>
          <div class="w-1/3 flex">
            <div class="w-1/3">Fare</div>
            <div class="w-1/3">Duration</div>
            <div class="w-1/3">Distance</div>
          </div>
          <div class="w-1/6">Status</div>
        </div>
        <div class="table-loader-container relative">
          <pt-list-item
            v-for="(tripRow, tripIndex) in tripsList"
            :key="tripIndex"
            :ref="`trip-row-${tripIndex}`"
            @list-item-expanded="onItemExpand(tripRow, tripIndex)"
          >
            <div class="flex flex-1 sm:flex-col lg:flex-row">
              <div class="sm:w-full lg:w-1/6 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-4 lg:mr-0">Requested On:</span>
                <span>{{ tripRow.created_at }}</span>
              </div>
              <div class="sm:w-full lg:w-1/6 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-4 lg:mr-0">Driver:</span>
                <router-link
                  :to="`/driver/${tripRow.driver_id}`"
                  class="font-bold no-underline text-black cursor-pointer hover:text-grey-darker"
                >{{ tripRow.driver_name }}</router-link>
              </div>
              <div class="sm:w-full lg:w-1/6 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-4 lg:mr-0">License:</span>
                <span>{{ tripRow.license }}</span>
              </div>
              <div class="flex sm:w-full lg:w-1/3 sm:flex-col lg:flex-row">
                <div class="sm:w-full lg:w-1/3 sm:mb-1 lg:mb-0">
                  <span class="font-bold lg:hidden sm:mr-4 lg:mr-0">Fare:</span>
                  <span>৳ {{ tripRow.fare }}</span>
                </div>
                <div class="sm:w-full lg:w-1/3 sm:mb-1 lg:mb-0">
                  <span class="font-bold lg:hidden sm:mr-4 lg:mr-0">Duration:</span>
                  <span>{{ getReadableTime(tripRow.duration) }}</span>
                </div>
                <div class="sm:w-full lg:w-1/3 sm:mb-1 lg:mb-0">
                  <span class="font-bold lg:hidden sm:mr-4 lg:mr-0">Distance:</span>
                  <span>{{ getReadableDistance(tripRow.distance) }}</span>
                </div>
              </div>
              <div :class="{'w-1/6 sm:mb-1 lg:mb-0': true, 'text-red': tripRow.status === 'CANCELED'}">
                <span class="font-bold lg:hidden sm:mr-4 lg:mr-0">Status:</span>
                <span class="uppercase">{{ tripRow.status }}{{ tripRow.canceled_by ? ` by ${tripRow.canceled_by}` : '' }}</span>
              </div>
            </div>
            <div
              slot="collapsible"
              class="w-full flex"
            >
              <pt-loader
                v-show="!trip.map_static_url"
                icon-class="fa-spinner text-xl"
              />
              <div
                v-show="trip.map_static_url"
                class="w-full flex sm:flex-col md:flex-row">
                <img style="width: 250px; height: 250px; border-radius: 15px">
                <div class="flex flex-1 flex-col m-4">
                  <h3>Pathao Ride</h3>
                  <div
                    v-if="trip.pickup_address"
                    class="flex my-4 items-center"
                  >
                    <div class="w-4 h-4 bg-green rounded-full mr-4" />
                    <div class="flex flex-1 flex-col">
                      <div
                        v-if="trip.start_time"
                        class="flex-1 text-lg font-bold"
                      >{{ trip.start_time }}</div>
                      <div
                        v-if="trip.pickup_address"
                        class="flex-1"
                      >{{ trip.pickup_address }}</div>
                    </div>
                  </div>
                  <div
                    v-if="trip.dropoff_address"
                    class="flex my-4 items-center"
                  >
                    <div class="w-4 h-4 bg-red rounded-full mr-4" />
                    <div class="flex flex-1 flex-col">
                      <div
                        v-if="trip.end_time"
                        class="flex-1  text-lg font-bold"
                      >{{ trip.end_time }}</div>
                      <div
                        v-if="trip.dropoff_address"
                        class="flex-1"
                      >{{ trip.dropoff_address }}</div>
                    </div>
                  </div>
                  <div
                    v-if="trip.cash_collected"
                    class="flex text-lg"
                  >
                    <div
                      v-if="trip.payment_type == 1"
                      class="font-bold ml-8 mr-2">Cash Collected:</div>
                    <div
                      v-if="trip.payment_type == 2"
                      class="font-bold ml-8 mr-2">Collected via Wallet:</div>
                    <div class="flex-1">৳ {{ trip.cash_collected }}</div>
                  </div>
                </div>
              </div>
            </div>
          </pt-list-item>
          <table-placeholder
            v-if="isLoadingTrips || !tripsList.length"
            :is-syncing="isLoadingTrips"
            :data-length="tripsList.length"
            loader-class="text-black"
          />
        </div>
      </pt-list>
      <pt-pagination
        :total-rows="totalRows"
        :current-page="currentPage"
        :page-size="pageSize"
        class="mt-8"
        @page-changed="fetchTripList"
      />
    </div>
  </layout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { PtDropdown, PtDropdownItem } from '@pathao/ui-components/esm/Dropdown';
import { PtRadioGroup, PtRadio } from '@pathao/ui-components/esm/FormComponents';
import { PtDatepicker } from '@pathao/ui-components/esm/Datepicker';
import { PtPagination } from '@pathao/ui-components/esm/Pagination';
import { PtLoader } from '@pathao/ui-components/esm/Loader';
import { PtList, PtListItem } from '@components/custom/List';
import Config from '@config';
import PaginationConfig from '@config/pagination';
import SuggestionInput from '@components/custom/SuggestionInput';
import TablePlaceholder from '@components/custom/TablePlaceholder';
import DownloadCsv from '@components/custom/DownloadCsv';
import Layout from '@layouts/main';
import makeRequest from '@utils/apiRequest';
import SelectedDates from '@utils/selected-dates';

import extendObj from 'lodash/extend';

const TRIPS_LIST_BASE_URL = `${Config.api.baseURL}api/partners/trips/list`;

export default {
  name: 'Trips',
  page: {
    title: 'Trips',
  },
  components: { PtDropdown, PtDropdownItem, PtDatepicker, Layout, PtList, PtListItem, PtRadioGroup, PtRadio, SuggestionInput, PtPagination, TablePlaceholder, PtLoader, DownloadCsv },
  data() {
    return {
      date: new Date(),
      selectedDateRangeType: 'daily',
      dateSelectionType: [
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' },
      ],
      tripsList: [],
      trip: {},
      totalRows: 0,
      currentPage: PaginationConfig.CURRENT_PAGE,
      pageSize: PaginationConfig.PAGE_SIZE,
      isLoadingTrips: false,
      filteredDriverId: null,
      selectedStatus: 'All',
    };
  },
  computed: {
    ...mapGetters({
      driverList: 'drivers/drivers',
    }),
    weekRange() {
      return this.selectedDateRangeType === 'weekly';
    },
    csvUrl() {
      const baseUrl = `${TRIPS_LIST_BASE_URL}/export`;
      const paramList = [];

      if (this.date) {
        const selectedDates = SelectedDates(this.date);

        if (selectedDates.filterByDate) {
          paramList.push(`filterByDate=${selectedDates['filterByDate']}`);
        } else {
          paramList.push(`filterByStartDate=${selectedDates['filterByStartDate']}`);
          paramList.push(`filterByEndDate=${selectedDates['filterByEndDate']}`);
        }
      }

      if (this.filteredDriverId) {
        paramList.push(`filterByDriverId=${this.filteredDriverId}`);
      }

      return paramList.length ? `${baseUrl}?${paramList.join('&')}` : baseUrl;
    },
    statusDropDownItem() {
      return {
        'All': 'All',
        'COMPLETED': 'COMPLETED',
        'CANCELED BY DRIVER': 'CANCELED BY DRIVER',
        'CANCELED BY USER': 'CANCELED BY USER',
      };
    },
  },
  watch: {
    selectedDateRangeType() {
      this.date = null;
    },
  },
  mounted() {
    this.fetchTripList();
    this.fetchDriverList();
  },
  methods: {
    ...mapActions({
      fetchDriverList: 'drivers/fetchDrivers',
    }),
    fetchTripList(page = PaginationConfig.CURRENT_PAGE, per_page = PaginationConfig.PAGE_SIZE) {
      this.tripsList = [];
      this.isLoadingTrips = true;
      const selectedDates = SelectedDates(this.date);

      let tripListParam = {
        page,
        per_page,
        ...selectedDates,
      };

      if (this.filteredDriverId) {
        tripListParam = extendObj({}, tripListParam, { filterByDriverId: this.filteredDriverId });
      }

      if (this.selectedStatus !== 'All') {
        tripListParam = extendObj({}, tripListParam, { filterByStatus: this.selectedStatus });
      }

      const tripsListMeta = {
        url: TRIPS_LIST_BASE_URL,
        params: tripListParam,
      };

      this.tableLoader = true;

      makeRequest(tripsListMeta)
        .then((listResponse) => {
          this.tripsList = listResponse.data.data;
          this.totalRows = listResponse.data.paginate.total;
          this.currentPage = listResponse.data.paginate.current_page;
          this.pageSize = listResponse.data.paginate.per_page;
          setTimeout(() => {}, 300000);
          this.isLoadingTrips = false;
        })
        .catch((errorResponse) => {
          this.$notification.error('Error in getting trips list');
          console.error(errorResponse);
          this.isLoadingTrips = false;
          setTimeout(() => {}, 300000);
        });
    },
    onItemExpand(trip, tripIndex) {
      const refKey = `trip-row-${tripIndex}`;
      const el = this.$refs[refKey][0].$el;
      const image = el.querySelector('.pt-collapsible-section img');

      if (image.src) {
        return;
      }

      const tripMeta = {
        url: `${Config.api.baseURL}api/partners/trips/${trip.id}`,
      };

      makeRequest(tripMeta)
        .then((response) => {
          this.trip = response.data;
          image.src = this.trip['map_static_url'];
        })
        .catch((errorResponse) => {
          this.$notification.error('Error in getting trip image');
          console.error(errorResponse);
        });
    },
    setDate(date) {
      this.date = date;
      this.fetchTripList();
    },
    onAutoCompleteItemCleared() {
      this.filteredDriverId = null;
      this.fetchTripList();
    },
    onAutoCompleteItemSelected(driver) {
      this.filteredDriverId = driver.id;
      this.fetchTripList(PaginationConfig.CURRENT_PAGE, PaginationConfig.PAGE_SIZE, { filterByDriverId: driver.id });
    },

    getReadableTime(time) {
      const seconds = time % 60;
      let minutes = time > 60 ? ((time - seconds) / 60) : 0;
      const hours = minutes > 60 ? Math.floor(minutes / 60) : 0;
      minutes = hours ? (minutes - (hours * 60)) : minutes;

      return hours ? `${hours} h ${minutes} m ${seconds} s` : minutes ? `${minutes} m ${seconds} s` : `${seconds} s`;
    },
    getReadableDistance(meter) {
      const km = Math.round(meter / 1000 * 100) / 100;
      return meter ? `${km} km` : `0 km`;
    },
    filterByStatus(status) {
      this.selectedStatus = status;
      this.fetchTripList();
    },
  },
};
</script>
